import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { ShowerHead, Package, Trash2, Droplets, Wrench } from 'lucide-react';
function GoldCheck() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5" data-id="element-341">
      <path d="M20 6L9 17L4 12" stroke="#C9952A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-342" />
    </svg>;
}
interface ServicesProps {
  language: 'en' | 'es';
}
export function Services({
  language
}: ServicesProps) {
  const t = {
    en: {
      eyebrow: 'WHAT WE RENT',
      heading: 'The Right Equipment. Ready When You Are.',
      requestQuote: 'GET A QUOTE',
      notSure: "Not sure what you need? We'll help you pick the right equipment.",
      ctaBtn: 'GET A QUOTE FOR YOUR PROJECT →',
      services: [{
        title: 'PORTABLE RESTROOMS',
        desc: 'Clean and reliable portable restroom rentals for construction sites, oilfield locations, outdoor events, and commercial projects across Odessa, Midland, and surrounding West Texas areas. Rentals include regular cleaning and servicing to help keep units clean, stocked, and ready for use throughout the rental period.',
        bullets: ['Oilfield & pipeline crews', 'Construction & job sites', 'Private & city events', 'Ranch properties']
      }, {
        title: 'COMBO TRAILER',
        desc: 'Trailer-mounted portable restrooms combined with trash collection. One unit, two solutions. Perfect for oilfield sites and large job sites that need sanitation and waste management together.',
        bullets: ['Restrooms + trash collection in one', 'Trailer mounted, easy to relocate', 'Ideal for oilfield and construction', 'Short and long term rentals']
      }, {
        title: 'TRASH TRAILER',
        desc: 'Dedicated trash collection trailers for job sites, oilfield locations, events, and ranches. Keep your site clean and compliant without the hassle.',
        bullets: ['Oilfield & job site waste management', 'Events and city cleanup', 'Ranch and rural properties', 'Regular pickup available']
      }, {
        title: 'PORTABLE WASH STATIONS',
        desc: 'Portable handwashing and sanitation stations for oilfield crews, construction sites, events, and anywhere clean hands matter. Serviced regularly on location.',
        bullets: ['Oilfield & construction crews', 'Events and public gatherings', 'Regular on-site servicing included', 'Standalone or combined with restrooms']
      }, {
        title: 'HDPE PIPE FUSION MACHINE',
        badge: 'McElroy 1" – 4" | Oilfield Ready',
        desc: 'McElroy pipe fusion machine rental for polyethylene pipeline work. Built for oilfield, water lines, and gas distribution across West Texas. Available 1" to 4" pipe.',
        bullets: ['Oilfield pipeline work', 'Water & gas line installation', 'McElroy machine, 1" to 4" pipe', 'Available for rent by the day or week']
      }]
    },
    es: {
      eyebrow: 'LO QUE RENTAMOS',
      heading: 'El Equipo Correcto. Listo Cuando Tú Lo Estés.',
      requestQuote: 'SOLICITAR COTIZACIÓN',
      notSure: '¿No estás seguro de lo que necesitas? Te ayudamos a elegir el equipo correcto.',
      ctaBtn: 'OBTÉN COTIZACIÓN PARA TU PROYECTO →',
      services: [{
        title: 'BAÑOS PORTÁTILES',
        desc: 'Renta de baños portátiles limpios y confiables para obras de construcción, sitios de oilfield, eventos al aire libre y proyectos comerciales en Odessa, Midland y las áreas circundantes de West Texas. Las rentas incluyen limpieza y servicio regular para ayudar a mantener las unidades limpias, surtidas y listas para usar durante todo el período de renta.',
        bullets: ['Cuadrillas de oilfield y tubería', 'Obras y sitios de construcción', 'Eventos privados y de ciudad', 'Propiedades de rancho']
      }, {
        title: 'REMOLQUE COMBO',
        desc: 'Baños portátiles montados en remolque combinados con recolección de basura. Una unidad, dos soluciones. Perfecto para sitios de oilfield y obras grandes que necesitan saneamiento y manejo de residuos juntos.',
        bullets: ['Baños + recolección de basura en uno', 'Montado en remolque, fácil de reubicar', 'Ideal para oilfield y construcción', 'Rentas a corto y largo plazo']
      }, {
        title: 'REMOLQUE DE BASURA',
        desc: 'Remolques dedicados a la recolección de basura para obras, sitios de oilfield, eventos y ranchos. Mantén tu sitio limpio y en orden sin complicaciones.',
        bullets: ['Manejo de residuos en oilfield y obras', 'Eventos y limpieza de ciudad', 'Propiedades de rancho y rurales', 'Recolección regular disponible']
      }, {
        title: 'ESTACIONES DE LAVADO PORTÁTILES',
        desc: 'Estaciones portátiles de lavado de manos y saneamiento para cuadrillas de oilfield, obras, eventos y donde la higiene importa. Con servicio regular en el lugar.',
        bullets: ['Cuadrillas de oilfield y construcción', 'Eventos y reuniones públicas', 'Servicio regular en sitio incluido', 'Independientes o combinadas con baños']
      }, {
        title: 'MÁQUINA DE FUSIÓN DE TUBERÍA HDPE',
        badge: 'McElroy 1" – 4" | Lista para Oilfield',
        desc: 'Renta de máquina de fusión McElroy para trabajo con tubería de polietileno. Hecha para oilfield, líneas de agua y distribución de gas en West Texas. Disponible para tubería de 1" a 4".',
        bullets: ['Trabajo de tubería en oilfield', 'Instalación de líneas de agua y gas', 'Máquina McElroy, tubería de 1" a 4"', 'Disponible por día o semana']
      }]
    }
  };
  const text = t[language];
  const icons = [ShowerHead, Package, Trash2, Droplets, Wrench];
  return <section id="services" className="py-12 md:py-20 bg-obsidian w-full overflow-hidden" data-id="element-343">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-344">
        <div className="text-center mb-10 md:mb-16" data-id="element-345">
          <span className="font-montserrat text-gold text-xs md:text-sm font-bold tracking-widest uppercase mb-3 md:mb-4 block" data-id="element-346">
            {text.eyebrow}
          </span>
          <h2 className="font-oswald text-3xl sm:text-4xl md:text-5xl font-bold text-warmWhite uppercase" data-id="element-347">
            {text.heading}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8" data-id="element-348">
          {text.services.map((service, index) => {
          const IconComponent = icons[index];
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={`bg-carbon rounded-lg p-6 md:p-8 border-t-4 border-gold border-x border-b border-steel flex flex-col h-full hover:border-gold/50 transition-colors text-center group ${index === text.services.length - 1 && text.services.length % 3 === 2 ? 'md:col-span-2 lg:col-span-1 lg:col-start-2' : ''}`} data-id="element-349">
                <div className="mb-5 flex justify-center" data-id="element-350">
                  <IconComponent size={48} color="#C9952A" strokeWidth={1.5} data-id="element-351" />
                </div>
                <h3 className="font-oswald text-2xl font-bold text-warmWhite mb-3 uppercase" data-id="element-352">
                  {service.title}
                </h3>
                {'badge' in service && service.badge && <span className="inline-block mx-auto mb-4 px-3 py-1 rounded-full bg-gold/10 border border-gold/30 font-montserrat text-gold text-xs font-bold tracking-wide" data-id="element-353">
                    {service.badge}
                  </span>}
                <p className="font-inter text-gray-400 mb-6 flex-grow text-sm md:text-base" data-id="element-354">
                  {service.desc}
                </p>
                <ul className="space-y-2 mb-6 inline-block text-left mx-auto" data-id="element-355">
                  {service.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-2 font-inter text-sm text-gray-300" data-id="element-356">
                      <GoldCheck data-id="element-357" />
                      <span data-id="element-358">{bullet}</span>
                    </li>)}
                </ul>
                <a href="#quote" className="inline-flex items-center justify-center gap-1 font-montserrat text-gold text-sm font-semibold tracking-wide hover:text-yellow-500 transition-colors mt-auto pt-2 border-t border-steel/50" data-id="element-359">
                  {text.requestQuote}{' '}
                  <span className="text-lg leading-none group-hover:translate-x-1 transition-transform duration-200" data-id="element-360">
                    →
                  </span>
                </a>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="text-center mt-10 md:mt-14" data-id="element-361">
          <p className="font-inter text-gray-400 text-base mb-5" data-id="element-362">
            {text.notSure}
          </p>
          <a href="#quote" className="inline-flex items-center justify-center gap-2 bg-gold text-obsidian px-10 py-4.5 rounded font-montserrat font-extrabold text-[16px] tracking-wide hover:bg-yellow-500 hover:scale-105 transition-all duration-300 shadow-lg shadow-gold/20" data-id="element-363">
            {text.ctaBtn}
          </a>
        </motion.div>
      </div>
    </section>;
}